/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 上传和读取处理数据到OSS的Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-08 11:36:38
 */

import { useCallback } from 'react'
import { useAppDispatch } from '../../../../../app/hooks'
import { uploadToOSS, fetchUploadToken } from '../../../../../actions/main'
// @ts-ignore
import md5 from 'js-md5'

interface UploadDataParams {
  originalUrl: string
  manualEditUrl: string
  remark: string
  autoUrl?: string // 自动处理的链接（如果有）
  [key: string]: any // 允许任意key的string值
}

interface ReadDataParams {
  originalUrl: string
  manualEditUrl: string
}

export interface ProcessedData {
  originalUrl: string
  manualEditUrl: string
  remark: string
  timestamp: string
  [key: string]: any // 允许任意key的值
}

// 数据key列表的OSS路径
const DATA_KEYS_LIST_OSS_KEY = 'watermark/batchEditor/test/data_keys.json'

/**
 * 上传和读取处理数据到OSS的Hook
 *
 * @example
 * const { uploadDataToOSS, readDataFromOSS } = useUploadDataToOSS()
 *
 * // 上传处理数据
 * await uploadDataToOSS({
 *   originalUrl: '...',
 *   manualEditUrl: '...',
 *   remark: '备注内容',
 *   autoUrl: '自动处理链接',
 *   isProcessed: true,
 *   processedBy: '用户名'
 * })
 *
 * // 读取处理数据
 * const data = await readDataFromOSS({
 *   originalUrl: '...',
 *   manualEditUrl: '...'
 * })
 */
export const useUploadDataToOSS = () => {
  const dispatch = useAppDispatch()

  const uploadDataToOSS = useCallback(
    async (params: UploadDataParams): Promise<string | null> => {
      const { originalUrl, manualEditUrl, remark, ...extraParams } = params

      try {
        // 拼接URL并进行MD5加密，生成文件名
        const urlString = `${originalUrl}${manualEditUrl}`
        const fileHash = (md5 as any)(urlString)

        console.log('fileHash:', fileHash)

        // 准备要上传的JSON数据，包含所有额外的参数
        const uploadData = {
          originalUrl,
          manualEditUrl,
          remark: remark || '',
          timestamp: new Date().toISOString(),
          ...extraParams // 包含所有额外的任意key的string值
        }

        // 将JSON数据转换为字符串（JSON.stringify默认使用UTF-8编码）
        const jsonString = JSON.stringify(uploadData, null, 2)

        // 使用TextEncoder将字符串转换为UTF-8字节数组，确保编码正确
        // 这样可以避免在OSS存储和读取时的编码问题
        const encoder = new TextEncoder()
        const jsonBytes = encoder.encode(jsonString)

        // 将UTF-8字节数组转换为Blob，明确指定UTF-8字符集
        const jsonBlob = new Blob([jsonBytes], {
          type: 'application/json; charset=UTF-8'
        })

        // 获取OSS token（使用net-cloud bucket）
        const bucketName = 'net-cloud'
        let ossToken = JSON.parse(localStorage.getItem('ossData') || '{}')
        if (
          !ossToken ||
          !ossToken[bucketName] ||
          ossToken[bucketName].expire < Date.now() / 1000
        ) {
          ossToken = await dispatch(fetchUploadToken())
        }

        if (!ossToken || !ossToken[bucketName]) {
          console.error('获取OSS token失败')
          throw new Error('获取OSS token失败')
        }

        // 生成OSS文件路径
        const ossKey = `watermark/batchEditor/test/${fileHash}.json`

        // 将Blob转换为File对象，确保使用UTF-8编码
        const jsonFile = new File([jsonBlob], `${fileHash}.json`, {
          type: 'application/json; charset=UTF-8'
        })

        // 上传到OSS
        // 注意：uploadToOSS 使用 https://net-cloud.oss-cn-beijing.aliyuncs.com
        // 必须使用 net-cloud bucket，并且policy需要允许上传到 watermark/batchEditor/test/ 路径

        await uploadToOSS({
          key: ossKey,
          OSSAccessKeyId: ossToken[bucketName]['accessid'],
          policy: ossToken[bucketName]['policy'],
          Signature: ossToken[bucketName]['signature'],
          file: jsonFile
        }).catch((err) => {
          console.error('OSS上传请求失败:', err)
          // 403错误通常是因为：
          // 1. policy配置不允许上传到该路径
          // 2. token过期或无效
          // 3. bucket名称不匹配
          throw new Error(
            `上传到OSS失败: ${err?.message || '请检查policy配置和文件路径权限'}`
          )
        })

        // 构建OSS URL
        const ossUrl = `https://net-cloud.xhey.top/${ossKey}`

        return ossUrl
      } catch (error) {
        console.error('上传处理数据到OSS失败:', error)
        throw error
      }
    },
    [dispatch]
  )

  const readDataFromOSS = useCallback(
    async (params: ReadDataParams): Promise<ProcessedData | null> => {
      const { originalUrl, manualEditUrl } = params

      try {
        // 拼接URL并进行MD5加密，生成文件名（与上传逻辑保持一致）
        const urlString = `${originalUrl}${manualEditUrl}`
        const fileHash = (md5 as any)(urlString)

        // 生成OSS文件路径（与上传逻辑保持一致）
        const ossKey = `watermark/batchEditor/test/${fileHash}.json`
        const ossUrl = `https://net-cloud.xhey.top/${ossKey}?t=${Date.now()}`

        // 从OSS读取JSON文件
        const response = await fetch(ossUrl)
        if (!response.ok) {
          if (response.status === 404) {
            console.log('处理数据不存在:', ossUrl)
            return null
          }
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const text = await response.text()
        const processedData = JSON.parse(text)
        return processedData
      } catch (error) {
        // 如果是404错误，返回null而不是抛出异常（文件不存在是正常情况）
        if (error instanceof Error && error.message.includes('404')) {
          console.log('处理数据不存在')
          return null
        }
        console.error('从OSS读取处理数据失败:', error)
        throw error
      }
    },
    []
  )

  /**
   * 上传数据key列表到OSS
   * @param keys key数组（MD5 hash值数组）
   */
  const uploadKeyList = useCallback(
    async (keys: string[]): Promise<string | null> => {
      try {
        // 准备要上传的key列表数据
        const keyListData = {
          keys: keys,
          timestamp: new Date().toISOString(),
          count: keys.length
        }

        // 将JSON数据转换为字符串
        const jsonString = JSON.stringify(keyListData, null, 2)

        // 使用TextEncoder确保UTF-8编码
        const encoder = new TextEncoder()
        const jsonBytes = encoder.encode(jsonString)

        // 将UTF-8字节数组转换为Blob
        const jsonBlob = new Blob([jsonBytes], {
          type: 'application/json; charset=UTF-8'
        })

        // 获取OSS token（使用net-cloud bucket）
        const bucketName = 'net-cloud'
        let ossToken = JSON.parse(localStorage.getItem('ossData') || '{}')
        if (
          !ossToken ||
          !ossToken[bucketName] ||
          ossToken[bucketName].expire < Date.now() / 1000
        ) {
          ossToken = await dispatch(fetchUploadToken())
        }

        if (!ossToken || !ossToken[bucketName]) {
          console.error('获取OSS token失败')
          throw new Error('获取OSS token失败')
        }

        // 将Blob转换为File对象
        const jsonFile = new File([jsonBlob], 'data_keys.json', {
          type: 'application/json; charset=UTF-8'
        })

        // 上传到OSS
        await uploadToOSS({
          key: DATA_KEYS_LIST_OSS_KEY,
          OSSAccessKeyId: ossToken[bucketName]['accessid'],
          policy: ossToken[bucketName]['policy'],
          Signature: ossToken[bucketName]['signature'],
          file: jsonFile
        }).catch((err) => {
          console.error('OSS上传key列表失败:', err)
          throw new Error(
            `上传key列表到OSS失败: ${
              err?.message || '请检查policy配置和文件路径权限'
            }`
          )
        })

        // 构建OSS URL
        const ossUrl = `https://net-cloud.xhey.top/${DATA_KEYS_LIST_OSS_KEY}`
        console.log('数据key列表已上传到OSS:', {
          ossKey: DATA_KEYS_LIST_OSS_KEY,
          ossUrl,
          keyCount: keys.length
        })

        return ossUrl
      } catch (error) {
        console.error('上传key列表到OSS失败:', error)
        throw error
      }
    },
    [dispatch]
  )

  /**
   * 从OSS读取数据key列表
   * @returns key数组，如果不存在则返回null
   */
  const readKeyList = useCallback(async (): Promise<string[] | null> => {
    try {
      const ossUrl = `https://net-cloud.xhey.top/${DATA_KEYS_LIST_OSS_KEY}?t=${Date.now()}`

      // 从OSS读取JSON文件
      const response = await fetch(ossUrl)
      if (!response.ok) {
        if (response.status === 404) {
          console.log('数据key列表不存在:', ossUrl)
          return null
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const text = await response.text()
      const keyListData = JSON.parse(text)

      // 返回key数组
      return keyListData.keys || []
    } catch (error) {
      // 如果是404错误，返回null而不是抛出异常（文件不存在是正常情况）
      if (error instanceof Error && error.message.includes('404')) {
        console.log('数据key列表不存在')
        return null
      }
      console.error('从OSS读取数据key列表失败:', error)
      throw error
    }
  }, [])

  /**
   * 根据原图URL和手动编辑URL生成MD5 hash key
   */
  const generateKey = useCallback(
    (originalUrl: string, manualEditUrl: string): string => {
      const urlString = `${originalUrl}${manualEditUrl}`
      return (md5 as any)(urlString)
    },
    []
  )

  return {
    uploadDataToOSS,
    readDataFromOSS,
    uploadKeyList,
    readKeyList,
    generateKey
  }
}

export default useUploadDataToOSS
